import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronRight, RefreshCw, Search, ShieldAlert, Wrench } from "lucide-react";

const BADGE = {
  MISSING_STOCK: "bg-rose-100 text-rose-700 border-rose-300",
  EXTRA_BATCHES: "bg-amber-100 text-amber-700 border-amber-300",
  NO_INVENTORY_LINES: "bg-slate-100 text-slate-600 border-slate-300",
  ASSET_REPAIR: "bg-slate-100 text-slate-600 border-slate-300",
  OK: "bg-emerald-100 text-emerald-700 border-emerald-300",
};
const LABEL = {
  MISSING_STOCK: "Missing Stock",
  EXTRA_BATCHES: "Extra Batches",
  NO_INVENTORY_LINES: "No Inventory Lines",
  ASSET_REPAIR: "Asset Repair PI",
  OK: "Stock OK",
};

export default function PiStockAuditPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback((query, all) => {
    setBusy(true);
    api.get("/pi-stock-audit", { params: { q: query || "", show: all ? "all" : "flagged" } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Audit failed"))
      .finally(() => setBusy(false));
  }, []);
  useEffect(() => { load("", false); }, [load]);

  async function repair(row) {
    setBusy(true);
    try {
      const r = await api.post("/pi-stock-audit/repair", { pi_id: row.pi_id });
      toast.success(`${row.invoice_number}: ${r.data.batches_created} stock batch(es) created`);
      load(q, showAll);
    } catch (e) { toast.error(e?.response?.data?.detail || "Repair failed"); setBusy(false); }
  }

  async function repairAll() {
    setBusy(true);
    try {
      const r = await api.post("/pi-stock-audit/repair-all", { confirm: true });
      toast.success(`${r.data.repaired} PI(s) repaired · ${r.data.total_batches_created} stock batch(es) created`);
      load(q, showAll);
    } catch (e) { toast.error(e?.response?.data?.detail || "Repair failed"); setBusy(false); }
  }

  const s = data?.summary;

  return (
    <div className="space-y-5" data-testid="pi-stock-audit-page">
      <PageHeader title="PI Stock Audit & Repair"
        subtitle="Scans every Approved Purchase Invoice and verifies each Inventory line created a stock batch. Flags misses with the exact reason and repairs them safely (line-level gap-fill — existing stock is never touched)." />

      {s && (
        <div className="grid grid-cols-5 gap-2 max-w-4xl">
          {[["PIs scanned", s.scanned], ["Stock OK", s.ok], ["Missing stock", s.missing_stock],
            ["Extra batches", s.extra_batches], ["Safe to repair", s.safe_to_sync]].map(([k, v]) => (
            <div key={k} className="bg-white border border-slate-200 px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{k}</div>
              <div className={`text-lg font-black tabular-nums ${k === "Missing stock" && v > 0 ? "text-rose-600" : "text-slate-900"}`}
                data-testid={`pi-audit-${k.toLowerCase().replace(/ /g, "-")}`}>{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input data-testid="pi-audit-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(q, showAll)}
            placeholder="Search invoice no (e.g. 0434)…"
            className="border border-slate-300 pl-7 pr-3 py-2 text-xs w-60" />
        </div>
        <button data-testid="pi-audit-refresh" onClick={() => load(q, showAll)} disabled={busy}
          className="border border-slate-300 text-slate-700 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 hover:border-[#F97316] hover:text-[#F97316] disabled:opacity-50">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Scan
        </button>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
          <input type="checkbox" data-testid="pi-audit-show-all" checked={showAll}
            onChange={(e) => { setShowAll(e.target.checked); load(q, e.target.checked); }} />
          Show all PIs (incl. OK)
        </label>
        {s?.safe_to_sync > 0 && (
          <button data-testid="pi-audit-repair-all" onClick={repairAll} disabled={busy}
            className="ml-auto bg-[#0B1F3A] text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 hover:bg-[#132a4d] disabled:opacity-50">
            <Wrench size={12} /> Repair All ({s.safe_to_sync} PIs)
          </button>
        )}
      </div>

      {!data ? <div className="p-8 text-sm text-slate-500">Scanning…</div> : (
        <div className="bg-white border border-slate-200">
          <div className="px-4 py-2.5 bg-[#0B1F3A] text-[10px] uppercase tracking-[0.2em] font-bold text-white">
            {showAll || q ? "Matching Purchase Invoices" : "Flagged Purchase Invoices"} · {data.rows.length}
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-500">
                {["", "Invoice No", "Date", "Vendor", "Branch", "Status", "Inv. Lines", "Batches", ""].map((h, i) => (
                  <th key={i} className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold ${i >= 6 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <>
                  <tr key={r.pi_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    data-testid={`pi-audit-row-${r.invoice_number.replace(/\//g, "-")}`}
                    onClick={() => setExpanded(expanded === r.pi_id ? null : r.pi_id)}>
                    <td className="pl-3 py-1.5 w-6 text-slate-400">
                      {expanded === r.pi_id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    </td>
                    <td className="px-3 py-1.5 font-semibold text-slate-800">{r.invoice_number}</td>
                    <td className="px-3 py-1.5 text-slate-500">{r.invoice_date}</td>
                    <td className="px-3 py-1.5">{r.vendor_name}</td>
                    <td className="px-3 py-1.5 text-slate-500">{r.branch_name}</td>
                    <td className="px-3 py-1.5">
                      <span className={`border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${BADGE[r.audit_status] || BADGE.OK}`}>
                        {LABEL[r.audit_status] || r.audit_status}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.expected_inventory_lines}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums font-bold ${r.batches_found < r.expected_inventory_lines ? "text-rose-600" : ""}`}>{r.batches_found}</td>
                    <td className="px-3 py-1.5 text-right">
                      {r.safe_to_sync ? (
                        <button data-testid={`pi-audit-sync-${r.invoice_number.replace(/\//g, "-")}`}
                          onClick={(e) => { e.stopPropagation(); repair(r); }} disabled={busy}
                          className="border border-[#0B1F3A] text-[#0B1F3A] px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold hover:bg-[#0B1F3A] hover:text-white disabled:opacity-50">
                          Sync Stock
                        </button>
                      ) : r.audit_status === "MISSING_STOCK" ? (
                        <span className="text-[10px] text-amber-700 font-bold uppercase">Manual review</span>
                      ) : null}
                    </td>
                  </tr>
                  {expanded === r.pi_id && (
                    <tr className="border-t border-slate-100 bg-slate-50/60">
                      <td colSpan={9} className="px-6 py-3 space-y-2">
                        {r.project_name && <div className="text-[11px] text-slate-500">Project: {r.project_name}</div>}
                        {r.missing_lines.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-rose-600 mb-1">Missing stock lines</div>
                            {r.missing_lines.map((l) => (
                              <div key={l.line_idx} className="text-[11px] text-slate-700 flex items-start gap-2 py-0.5">
                                <AlertTriangle size={11} className="text-rose-500 mt-0.5 shrink-0" />
                                <span><b>{l.item_code}</b> — {l.description} · {l.qty} {l.unit} · <span className="text-rose-700">{l.reason}</span></span>
                              </div>
                            ))}
                          </div>
                        )}
                        {r.unmapped_legacy_batches > 0 && (
                          <div className="text-[11px] text-amber-700">{r.unmapped_legacy_batches} legacy batch(es) can't be mapped to a specific line — auto-repair disabled to avoid duplicates.</div>
                        )}
                        {r.fully_consumed_batches > 0 && (
                          <div className="text-[11px] text-slate-500">{r.fully_consumed_batches} batch(es) fully issued (remaining 0) — they won't appear on the Stock page but the receipt did happen.</div>
                        )}
                        {r.info_lines.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Lines with no stock expected</div>
                            {r.info_lines.map((l) => (
                              <div key={l.line_idx} className="text-[11px] text-slate-600 py-0.5">
                                <b>{l.item_code}</b> — {l.description} · {l.qty} {l.unit} · {l.why}
                              </div>
                            ))}
                          </div>
                        )}
                        {(r.side_effect_errors || []).length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-rose-600 mb-1">Recorded approval errors</div>
                            {r.side_effect_errors.map((e, i) => (
                              <div key={i} className="text-[11px] font-mono text-rose-700">line {e.line_idx}: {e.error}</div>
                            ))}
                          </div>
                        )}
                        {r.audit_status === "OK" && <div className="text-[11px] text-emerald-700 font-bold">All inventory lines have stock batches.</div>}
                      </td>
                    </tr>
                  )}
                </>
              ))}
              {!data.rows.length && (
                <tr><td colSpan={9} className="px-4 py-5 text-center text-emerald-700 font-bold">
                  No issues found — every approved PI's inventory lines have stock batches
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 max-w-2xl flex items-start gap-2">
        <ShieldAlert size={13} className="mt-0.5 shrink-0" />
        Repair uses per-line gap-fill: lines that already have a batch are skipped, only missing
        lines get new stock. Items typed Service/Asset never create stock — fix the item type in
        the Item master first, then Sync.
      </p>
    </div>
  );
}
